import { injectable } from 'inversify';
import type { FormInstance } from '@shared/common/form/domain/model/form-instance.model';
import type { FormField } from '@shared/common/form/domain/form/field/union.form-field';
import type { TranslatedTextLocale } from '@shared/common/translation/domain/model/translated-text.model';
import type { CustomisationPreviewElements } from '@shared/common/customisation-preview/domain/model/customisation-preview-elements.model';
import {
  CustomisationPreviewFormControlKind,
  CustomisationPreviewFormFieldMetaKey,
  type CustomisationPreviewFormFieldMeta,
} from '@shared/common/customisation-preview/domain/constant/customisation-preview-form-control-kind.constant';

@injectable()
export class FreeTextLocaleSynchronizationService {
  public apply<TFormModel, TVariant extends string, TRole extends string>(
    formInstance: FormInstance<TFormModel>,
    customisationPreviewElements: CustomisationPreviewElements<TVariant, TRole>,
    locale: TranslatedTextLocale,
  ): void {
    const fields = Object.values(formInstance.fields) as Array<FormField<unknown> | undefined>;

    for (const field of fields) {
      if (!field) {
        continue;
      }

      const meta = this.getCustomisationPreviewMeta<TRole>(field);
      if (!meta) {
        continue;
      }

      if (field.type !== 'text') {
        continue;
      }

      field.value = customisationPreviewElements.resolveTextValue(meta.role, locale);
    }
  }

  private getCustomisationPreviewMeta<TRole extends string>(
    field: FormField<unknown>,
  ): CustomisationPreviewFormFieldMeta<TRole> | null {
    const metaValue = field.meta?.[CustomisationPreviewFormFieldMetaKey];
    if (!metaValue || typeof metaValue !== 'object') {
      return null;
    }

    const meta = metaValue as Partial<CustomisationPreviewFormFieldMeta<TRole>>;
    if (meta.controlKind !== CustomisationPreviewFormControlKind.FreeText) {
      return null;
    }

    if (typeof meta.role !== 'string') {
      return null;
    }

    return meta as CustomisationPreviewFormFieldMeta<TRole>;
  }
}
